import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const marineLinks = [
  { to: "/peme-review", label: "PEME Review" },
  { to: "/clinic-audit", label: "Clinic Audit" },
];

const NavItem = ({ to, label, testid }) => (
  <NavLink to={to} data-testid={testid} className="relative text-sm font-semibold text-[#1a3a6b] hover:text-[#2ab5cf] transition-colors">
    {({ isActive }) =>
      isActive ? (
        <span className="relative inline-flex flex-col items-center animate-underline" data-testid={`${testid}-active-laptop`}>
          <span className="px-2.5 py-1 border-2 border-[#2ab5cf] rounded-md text-[#2ab5cf] bg-[#2ab5cf]/5 leading-none">
            {label}
          </span>
          <span
            className="mt-[3px] h-[4px] w-[128%] bg-[#2ab5cf] rounded-b-sm"
            style={{ clipPath: "polygon(7% 0, 93% 0, 100% 100%, 0 100%)" }}
          />
        </span>
      ) : (
        <span className="inline-block">{label}</span>
      )
    }
  </NavLink>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-4 inset-x-0 z-50 px-4 md:px-8">
      <div className="max-w-6xl mx-auto rounded-full backdrop-blur-xl bg-white/70 border border-white/80 shadow-lg shadow-[#1a3a6b]/5 h-20 md:h-24 px-5 md:px-8 flex items-center justify-between transition-shadow duration-300 hover:shadow-xl hover:shadow-[#1a3a6b]/10" data-testid="floating-navbar">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center">
          <img src="/medverify-logo.png" alt="MedVerify Services" className="h-16 md:h-20 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavItem to="/" label="Corporate Wellness" testid="nav-link-corporate-wellness" />

          <div className="relative group" data-testid="nav-marine-care">
            <button className="text-sm font-semibold text-[#1a3a6b] group-hover:text-[#2ab5cf] transition-colors inline-flex items-center gap-1.5">
              Marine Care <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
              <div className="backdrop-blur-xl bg-white/85 border border-white/80 rounded-2xl shadow-xl shadow-[#1a3a6b]/10 p-2 min-w-[190px]">
                {marineLinks.map((m) => (
                  <NavLink
                    key={m.to}
                    to={m.to}
                    data-testid={`nav-link-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                        isActive ? "text-[#2ab5cf] bg-[#2ab5cf]/8" : "text-[#1a3a6b] hover:bg-[#2ab5cf]/8 hover:text-[#2ab5cf]"
                      }`
                    }
                  >
                    {m.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          <NavItem to="/about-us" label="About Us" testid="nav-link-about-us" />
          <a
            href="mailto:info@medverifys.com"
            data-testid="nav-contact-button"
            className="btn-primary rounded-full px-6 py-2.5 text-sm font-bold"
          >
            Contact Us
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#1a3a6b]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <nav className="md:hidden max-w-6xl mx-auto mt-3 rounded-3xl backdrop-blur-xl bg-white/85 border border-white/80 shadow-xl shadow-[#1a3a6b]/10 px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          <NavLink to="/" onClick={() => setOpen(false)} className="text-base font-semibold text-[#1a3a6b]">
            Corporate Wellness
          </NavLink>
          <p className="text-base font-semibold text-slate-400">Marine Care</p>
          {marineLinks.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              onClick={() => setOpen(false)}
              className="text-base font-semibold text-[#1a3a6b] pl-5"
            >
              {m.label}
            </NavLink>
          ))}
          <NavLink to="/about-us" onClick={() => setOpen(false)} className="text-base font-semibold text-[#1a3a6b]">
            About Us
          </NavLink>
          <a href="mailto:info@medverifys.com" className="btn-primary rounded-full px-6 py-3 text-sm font-bold text-center">
            Contact Us
          </a>
        </nav>
      )}
    </header>
  );
}
